import { AxiosRequestConfig } from 'axios'
import {
	type TBillingTransaction,
	type TPaymentDetailsResponse,
} from 'types/billing'
import { type DefaultApiResponse } from 'types/index'

import { getApiClient } from '@/api'
import {
	API_ROUTES,
	OVERRIDE_COUNTRY_CODE_HEADER,
	STABLE_ID_COOKIE,
} from '@/constants'
import { getCountryFromHeaders } from '@/utils'
import { getCookieClient } from '@/utils/client'

import {
	type CreatePayPalSubscriptionResponse,
	type CreateStripeSubscriptionResponse,
	type CreateSubscriptionRequestParams,
	type Currency,
	type Subscription,
	type SubscriptionProduct,
} from '../../types/subscription'
import { saveCheckoutMetadata } from './checkoutMetadata'

// MYQRCODE-10819 — attaches the StableID (MYQRCODE-10818) to a subscription
// creation payload so the backend can later attribute purchase_success back
// to the experiment assignment. Omitted entirely (not sent as undefined) if
// the cookie is missing, since these three endpoints predate this field.
const withStableId = <T extends object>(payload: T): T => {
	const stableId = getCookieClient(STABLE_ID_COOKIE)

	return stableId ? { ...payload, stableId } : payload
}

export const subscriptionApi = {
	async getSubscriptionProducts(
		currencyCode?: string,
		experimental = false,
		headers?: Headers,
		locale?: string,
	): Promise<SubscriptionProduct[]> {
		const request: AxiosRequestConfig = {
			headers: {
				'x-experimental': String(experimental),
			},
		} as AxiosRequestConfig

		if (request.headers) {
			if (locale) {
				request.headers['x-locale'] = locale
			}
			if (currencyCode) {
				request.headers['x-currency'] = currencyCode
			} else if (headers) {
				const forwardedCurrency = headers.get('x-currency')
				if (forwardedCurrency) {
					request.headers['x-currency'] = forwardedCurrency
				}
			}
		}

		if (headers && request.headers) {
			const ipCountry = getCountryFromHeaders(headers)
			if (ipCountry) {
				request.headers['cf-ipcountry'] = ipCountry
				request.headers[OVERRIDE_COUNTRY_CODE_HEADER] = ipCountry
			}
			const ipCity = headers.get('cf-ipcity')
			if (ipCity) {
				request.headers['cf-ipcity'] = ipCity
			}
		}

		const { data } = await getApiClient().get(
			API_ROUTES.subscription.products,
			request,
		)

		return data
	},

	async getSubscriptionProductsForUser(
		{
			fromPage,
			currencyCode,
			experimental = false,
			shoulShowNotification = true,
			locale,
		}: {
			fromPage: string
			currencyCode?: string
			experimental?: boolean
			shoulShowNotification?: boolean
			locale?: string
		},
		headers?: Headers,
	): Promise<SubscriptionProduct[]> {
		const request: AxiosRequestConfig = {
			shouldShowErrorNotification: shoulShowNotification,
			headers: {
				'x-experimental': String(experimental),
			},
		} as AxiosRequestConfig

		if (request.headers && locale) {
			request.headers['x-locale'] = locale
		}

		if (request.headers && currencyCode) {
			request.headers['x-currency'] = currencyCode
		}

		if (request.headers && headers) {
			const hasGet = typeof headers?.get === 'function'
			const ipCountry = getCountryFromHeaders(headers)
			if (!currencyCode && headers) {
				const forwardedCurrency = hasGet
					? (headers.get('x-currency') ?? '')
					: ((headers as unknown as { 'x-currency': string })[
							'x-currency'
						] ?? '')
				if (forwardedCurrency) {
					request.headers['x-currency'] = forwardedCurrency
				}
			}
			if (headers) {
				const resolvedIpCountry = ipCountry ?? ''
				if (resolvedIpCountry) {
					request.headers['cf-ipcountry'] = resolvedIpCountry
					request.headers[OVERRIDE_COUNTRY_CODE_HEADER] =
						resolvedIpCountry
				}
				const resolvedIpCity = hasGet
					? (headers.get('cf-ipcity') ?? '')
					: ((headers as unknown as { 'cf-ipcity': string })[
							'cf-ipcity'
						] ?? '')
				if (resolvedIpCity) {
					request.headers['cf-ipcity'] = resolvedIpCity
				}
			}
		}

		const { data } = await getApiClient().get(
			`${API_ROUTES.subscription.userProducts}?fromPage=${fromPage}`,
			request,
		)

		return data
	},

	async getCurrencies(cfIpCountry?: string | null): Promise<Currency[]> {
		const { data } = await getApiClient().get(
			API_ROUTES.subscription.currencies,
			{
				headers: {
					'Cf-Ipcountry': cfIpCountry,
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache',
					Expires: '0',
				},
			},
		)

		return data
	},

	async createStripeSubscription(
		payload: CreateSubscriptionRequestParams,
	): Promise<CreateStripeSubscriptionResponse> {
		await saveCheckoutMetadata()

		const { data } = await getApiClient().post(
			API_ROUTES.subscription.createStripe,
			withStableId(payload),
			{
				headers: {
					'X-Client-Cookies': document.cookie,
				},
			},
		)

		return data
	},

	async createPayPalSubscription(
		payload: Omit<CreateSubscriptionRequestParams, 'paymentMethodId'>,
	): Promise<CreatePayPalSubscriptionResponse> {
		await saveCheckoutMetadata()

		const { data } = await getApiClient().post(
			API_ROUTES.subscription.createPayPal,
			withStableId(payload),
		)

		return data
	},

	async createPayPalIncompleteSubscription(
		payload: Omit<CreateSubscriptionRequestParams, 'paymentMethodId'>,
	): Promise<CreatePayPalSubscriptionResponse> {
		await saveCheckoutMetadata()

		const { data } = await getApiClient().post(
			API_ROUTES.subscription.createPayPalInitial,
			withStableId(payload),
		)

		return data
	},

	async getCurrentSubscription(): Promise<Subscription> {
		const { data } = await getApiClient().get(
			API_ROUTES.subscription.current,
		)

		return data
	},

	async getTransactions(): Promise<TBillingTransaction[]> {
		const { data } = await getApiClient().get(
			API_ROUTES.billing.transactions,
		)

		return data
	},

	async cancelSubscription(
		subscriptionId: string,
	): Promise<DefaultApiResponse> {
		const { data } = await getApiClient().post(
			API_ROUTES.subscription.cancel(subscriptionId),
		)

		return data
	},

	async reactivateSubscription(
		subscriptionId: string,
	): Promise<DefaultApiResponse> {
		const { data } = await getApiClient().post(
			API_ROUTES.subscription.reactivate(subscriptionId),
		)

		return data
	},

	async paymentDetails(): Promise<TPaymentDetailsResponse> {
		const { data } = await getApiClient().get(
			API_ROUTES.billing.paymentDetails,
		)

		return data
	},

	async getTransactionInvoice(
		transactionId: string,
		timezone?: string,
	): Promise<string> {
		const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
		const { data } = await getApiClient().get(
			API_ROUTES.billing.invoice(transactionId),
			{
				params: { timezone: tz },
				responseType: 'blob',
			},
		)

		return data
	},

	async cancelPublicSubscriptionRequest(
		email: string,
		captchaToken?: string,
		source?: string,
	): Promise<{ message: string; cancelSubscriptionRequestId: string }> {
		const { data } = await getApiClient().post(
			API_ROUTES.subscription.common.cancelRequest,
			{ email, captchaToken },
			// MYQRCODE-10803 AC#3: sent as a query param, not a body field —
			// qr-api's ValidationPipe (forbidNonWhitelisted) would 400 an
			// unrecognized body field, but doesn't touch the query string.
			{ params: { src: source } },
		)

		return data
	},

	async cancelPublicSubscription(
		subscriptionId: string,
	): Promise<DefaultApiResponse> {
		const { data } = await getApiClient().post(
			API_ROUTES.subscription.common.cancel(subscriptionId),
		)

		return data
	},
}
